import React, { useState, useEffect } from 'react';
import { HiCheck, HiOutlineLocationMarker, HiOutlineMail, HiOutlinePhone, HiOutlineGlobe } from 'react-icons/hi';
import { contactService } from '../../services/contactService';
import './styles/ContactManagement.css';

function ContactManagement({ selectedEdition }) {
  const [contactData, setContactData] = useState({
    heroSection: {
      title: 'Contactez-nous',
      subtitle: 'N\'hésitez pas à nous poser vos questions'
    },
    formSection: {
      title: 'Envoyez-nous un message',
      subtitle: 'Nous vous répondrons dans les plus brefs délais',
      submitButtonText: 'Envoyer le message'
    },
    contactInfo: {
      title: 'Informations de contact',
      venue: {
        title: 'Lieu',
        address: '',
        subAddress: ''
      },
      email: {
        title: 'Email',
        primary: '',
        secondary: ''
      },
      phone: {
        title: 'Téléphone',
        primary: '',
        secondary: ''
      },
      social: {
        title: 'Réseaux sociaux',
        website: '',
        handle: ''
      }
    },
    mapSection: {
      title: 'Localisation',
      description: 'Retrouvez-nous sur la carte',
      coordinates: {
        lat: 33.5731,
        lng: -7.5898
      },
      popupText: 'Université Hassan II'
    }
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);

  useEffect(() => {
    if (selectedEdition) {
      loadContactData();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(contactData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [contactData, originalData]);

  const loadContactData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      console.log('Loading contact data for edition:', selectedEdition);
      const data = await contactService.getContactData(selectedEdition);
      
      if (!data || ((!data.heroSection?.title || data.heroSection.title === 'Contactez-nous') && 
                    (!data.heroSection?.subtitle || data.heroSection.subtitle === 'N\'hésitez pas à nous poser vos questions') &&
                    (!data.contactInfo?.venue?.address || data.contactInfo.venue.address === '') &&
                    (!data.contactInfo?.email?.primary || data.contactInfo.email.primary === '') &&
                    (!data.contactInfo?.phone?.primary || data.contactInfo.phone.primary === ''))) {
        console.log('No data found, setting defaults');
        setIsDatabaseEmpty(true);
        const defaultData = {
          heroSection: {
            title: 'Contactez-nous',
            subtitle: 'Nous sommes là pour répondre à toutes vos questions concernant le congrès GSIDA'
          },
          formSection: {
            title: 'Envoyez-nous un message',
            subtitle: 'Nous vous répondrons dans les plus brefs délais',
            submitButtonText: 'Envoyer le message'
          },
          contactInfo: {
            title: 'Informations de contact',
            venue: {
              title: 'Lieu',
              address: 'Université Hassan II',
              subAddress: 'Casablanca, Maroc'
            },
            email: {
              title: 'Email',
              primary: 'contact@gsida.ma',
              secondary: 'info@gsida.ma'
            },
            phone: {
              title: 'Téléphone',
              primary: '+212 522 123 456',
              secondary: '+212 661 234 567'
            },
            social: {
              title: 'Réseaux sociaux',
              website: 'www.gsida.ma',
              handle: '@GSIDA_Maroc'
            }
          },
          mapSection: {
            title: 'Localisation',
            description: 'Retrouvez-nous sur la carte pour plus de facilité',
            coordinates: {
              lat: 33.5731,
              lng: -7.5898
            },
            popupText: 'Université Hassan II - Casablanca'
          }
        };
        setContactData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
      } else {
        console.log('Data found:', data);
        setContactData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading contact data:', error);
      setIsDatabaseEmpty(true);
      const defaultData = {
        heroSection: {
          title: 'Contactez-nous',
          subtitle: 'Nous sommes là pour répondre à toutes vos questions concernant le congrès GSIDA'
        },
        formSection: {
          title: 'Envoyez-nous un message',
          subtitle: 'Nous vous répondrons dans les plus brefs délais',
          submitButtonText: 'Envoyer le message'
        },
        contactInfo: {
          title: 'Informations de contact',
          venue: {
            title: 'Lieu',
            address: 'Université Hassan II',
            subAddress: 'Casablanca, Maroc'
          },
          email: {
            title: 'Email',
            primary: 'contact@gsida.ma',
            secondary: 'info@gsida.ma'
          },
          phone: {
            title: 'Téléphone',
            primary: '+212 522 123 456',
            secondary: '+212 661 234 567'
          },
          social: {
            title: 'Réseaux sociaux',
            website: 'www.gsida.ma',
            handle: '@GSIDA_Maroc'
          }
        },
        mapSection: {
          title: 'Localisation',
          description: 'Retrouvez-nous sur la carte pour plus de facilité',
          coordinates: {
            lat: 33.5731,
            lng: -7.5898
          },
          popupText: 'Université Hassan II - Casablanca'
        }
      };
      setContactData(defaultData);
      setOriginalData(JSON.parse(JSON.stringify(defaultData)));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await contactService.updateContactData(contactData, selectedEdition);
      setOriginalData(JSON.parse(JSON.stringify(contactData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      showSuccessMessage();
    } catch (error) {
      console.error('Error saving contact data:', error);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setContactData(JSON.parse(JSON.stringify(originalData)));
      setHasUnsavedChanges(false);
    }
  };

  const updateContactData = (updates) => {
    setContactData(prev => ({ ...prev, ...updates }));
  };

  const handleUpdateHeroSection = (field, value) => {
    updateContactData({
      heroSection: {
        ...contactData.heroSection,
        [field]: value
      }
    });
  };

  const handleUpdateFormSection = (field, value) => {
    updateContactData({
      formSection: {
        ...contactData.formSection,
        [field]: value
      }
    });
  };

  const handleUpdateContactInfo = (section, field, value) => {
    if (section === 'title') {
      updateContactData({
        contactInfo: {
          ...contactData.contactInfo,
          title: value
        }
      });
    } else {
      updateContactData({
        contactInfo: {
          ...contactData.contactInfo,
          [section]: {
            ...contactData.contactInfo[section],
            [field]: value
          }
        }
      });
    }
  };

  const handleUpdateMapSection = (field, value) => {
    if (field === 'lat' || field === 'lng') {
      updateContactData({
        mapSection: {
          ...contactData.mapSection,
          coordinates: {
            ...contactData.mapSection.coordinates,
            [field]: parseFloat(value) || 0
          }
        }
      });
    } else {
      updateContactData({
        mapSection: {
          ...contactData.mapSection,
          [field]: value
        }
      });
    }
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Contact est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour initialiser les données.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Hero Section */}
      <div className="section-header">
        <h3>Section Héro</h3>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label>Titre principal:</label>
          <input 
            type="text" 
            value={contactData.heroSection.title}
            onChange={(e) => handleUpdateHeroSection('title', e.target.value)}
            placeholder="Ex: Contactez-nous"
          />
        </div>
        <div className="form-group">
          <label>Sous-titre:</label>
          <textarea 
            value={contactData.heroSection.subtitle}
            onChange={(e) => handleUpdateHeroSection('subtitle', e.target.value)}
            placeholder="Description ou message d'accueil"
          />
        </div>
      </div>

      {/* Form Section */}
      <div className="section-header">
        <h3>Section Formulaire</h3>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label>Titre du formulaire:</label>
          <input 
            type="text" 
            value={contactData.formSection.title}
            onChange={(e) => handleUpdateFormSection('title', e.target.value)}
            placeholder="Ex: Envoyez-nous un message"
          />
        </div>
        <div className="form-group">
          <label>Sous-titre du formulaire:</label>
          <input 
            type="text" 
            value={contactData.formSection.subtitle}
            onChange={(e) => handleUpdateFormSection('subtitle', e.target.value)}
            placeholder="Ex: Nous vous répondrons rapidement"
          />
        </div>
        <div className="form-group">
          <label>Texte du bouton:</label>
          <input 
            type="text" 
            value={contactData.formSection.submitButtonText}
            onChange={(e) => handleUpdateFormSection('submitButtonText', e.target.value)}
            placeholder="Ex: Envoyer le message"
          />
        </div>
      </div>

      {/* Contact Information */}
      <div className="section-header">
        <h3>Informations de Contact</h3>
      </div>
      <div className="form-grid">
        <div className="form-group form-group-full">
          <label>Titre de la section:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.title}
            onChange={(e) => handleUpdateContactInfo('title', '', e.target.value)}
            placeholder="Ex: Informations de contact"
          />
        </div>

        {/* Venue Information */}
        <div className="form-group">
          <label>Titre du lieu:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.venue.title}
            onChange={(e) => handleUpdateContactInfo('venue', 'title', e.target.value)}
            placeholder="Ex: Lieu"
          />
        </div>
        <div className="form-group">
          <label>Adresse principale:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.venue.address}
            onChange={(e) => handleUpdateContactInfo('venue', 'address', e.target.value)}
            placeholder="Ex: Université Hassan II"
          />
        </div>
        <div className="form-group form-group-full">
          <label>Sous-adresse:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.venue.subAddress}
            onChange={(e) => handleUpdateContactInfo('venue', 'subAddress', e.target.value)}
            placeholder="Ex: Casablanca, Maroc"
          />
        </div>

        {/* Email Information */}
        <div className="form-group">
          <label>Titre email:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.email.title}
            onChange={(e) => handleUpdateContactInfo('email', 'title', e.target.value)}
            placeholder="Ex: Email"
          />
        </div>
        <div className="form-group">
          <label>Email principal:</label>
          <input 
            type="email" 
            value={contactData.contactInfo.email.primary}
            onChange={(e) => handleUpdateContactInfo('email', 'primary', e.target.value)}
            placeholder="Ex: contact@gsida.ma"
          />
        </div>
        <div className="form-group form-group-full">
          <label>Email secondaire:</label>
          <input 
            type="email" 
            value={contactData.contactInfo.email.secondary}
            onChange={(e) => handleUpdateContactInfo('email', 'secondary', e.target.value)}
            placeholder="Ex: info@gsida.ma"
          />
        </div>

        {/* Phone Information */}
        <div className="form-group">
          <label>Titre téléphone:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.phone.title}
            onChange={(e) => handleUpdateContactInfo('phone', 'title', e.target.value)}
            placeholder="Ex: Téléphone"
          />
        </div>
        <div className="form-group">
          <label>Téléphone principal:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.phone.primary}
            onChange={(e) => handleUpdateContactInfo('phone', 'primary', e.target.value)}
            placeholder="Ex: +212 522 123 456"
          />
        </div>
        <div className="form-group form-group-full">
          <label>Téléphone secondaire:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.phone.secondary}
            onChange={(e) => handleUpdateContactInfo('phone', 'secondary', e.target.value)}
            placeholder="Ex: +212 661 234 567"
          />
        </div>

        {/* Social Information */}
        <div className="form-group">
          <label>Titre réseaux sociaux:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.social.title}
            onChange={(e) => handleUpdateContactInfo('social', 'title', e.target.value)}
            placeholder="Ex: Réseaux sociaux"
          />
        </div>
        <div className="form-group">
          <label>Site web:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.social.website}
            onChange={(e) => handleUpdateContactInfo('social', 'website', e.target.value)}
            placeholder="Ex: www.gsida.ma"
          />
        </div>
        <div className="form-group form-group-full">
          <label>Handle social:</label>
          <input 
            type="text" 
            value={contactData.contactInfo.social.handle}
            onChange={(e) => handleUpdateContactInfo('social', 'handle', e.target.value)}
            placeholder="Ex: @GSIDA_Maroc"
          />
        </div>
      </div>

      {/* Map Section */}
      <div className="section-header">
        <h3>Section Carte</h3>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label>Titre de la carte:</label>
          <input 
            type="text" 
            value={contactData.mapSection.title}
            onChange={(e) => handleUpdateMapSection('title', e.target.value)}
            placeholder="Ex: Localisation"
          />
        </div>
        <div className="form-group">
          <label>Description de la carte:</label>
          <input 
            type="text" 
            value={contactData.mapSection.description}
            onChange={(e) => handleUpdateMapSection('description', e.target.value)}
            placeholder="Ex: Retrouvez-nous sur la carte"
          />
        </div>
        <div className="form-group form-group-full">
          <label>Texte du popup:</label>
          <textarea 
            value={contactData.mapSection.popupText}
            onChange={(e) => handleUpdateMapSection('popupText', e.target.value)}
            placeholder="Texte affiché dans la bulle de la carte"
          />
        </div>
        <div className="form-group">
          <label>Latitude:</label>
          <input 
            type="number" 
            step="0.000001"
            value={contactData.mapSection.coordinates.lat}
            onChange={(e) => handleUpdateMapSection('lat', e.target.value)}
            placeholder="Ex: 33.5731"
          />
        </div>
        <div className="form-group">
          <label>Longitude:</label>
          <input 
            type="number" 
            step="0.000001"
            value={contactData.mapSection.coordinates.lng}
            onChange={(e) => handleUpdateMapSection('lng', e.target.value)}
            placeholder="Ex: -7.5898"
          />
        </div>
      </div>
    </div>
  );
}

export default ContactManagement;
